import { useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Plus } from 'lucide-react';
import { apiRequest } from '@/lib/queryClient';
import { useToast } from '@/hooks/use-toast';
import type { AisleWalkthrough, StoreProfileDetail, WalkthroughMarker } from '../../types';
import { WalkthroughVideoPlayer, formatWalkthroughDuration } from '../WalkthroughVideoPlayer';
import { WalkthroughUploadPanel } from '../WalkthroughUploadPanel';
import { WalkthroughRecordEditor } from '../WalkthroughRecordEditor';
import { WalkthroughCardPreview } from '../WalkthroughCardPreview';
import { WalkthroughAisleTagsPanel } from '../WalkthroughAisleTagsPanel';
import { resolveWalkthroughVideoUrl } from '../../mock-data/walkthrough-videos';
import { resolveWalkthroughLocationBadges } from '../../lib/inStoreLocationLabels';
import {
  normalizeVideoRotationDegrees,
  type VideoRotationDegrees,
} from '../../lib/walkthroughVideoRotation';

interface AisleWalkthroughsTabProps {
  profile: StoreProfileDetail;
  storeId?: number;
  storeIdParam?: string;
  focusedAisleNumber?: number | null;
  onFocusAisle?: (aisleNumber: number | null) => void;
  onOpenDigitalTwin?: (aisleNumber: number) => void;
}

/** Single walkthrough status model — replaces the old overlapping Ready/Uploaded/Processed badges. */
const STATUS_LABELS: Record<AisleWalkthrough['processingStatus'], string> = {
  processed: 'Ready',
  processing: 'Processing',
  failed: 'Failed',
};

const statusColors: Record<AisleWalkthrough['processingStatus'], string> = {
  processed: 'bg-green-100 text-green-800',
  processing: 'bg-blue-100 text-blue-800',
  failed: 'bg-red-100 text-red-800',
};

function locationIdForAisleNumber(
  aisleNumber: number,
  aisles: StoreProfileDetail['aisles'],
): number | null {
  const aisle = aisles.find((item) => item.number === aisleNumber);
  if (!aisle?.id.startsWith('live-aisle-')) return null;
  const parsed = parseInt(aisle.id.replace('live-aisle-', ''), 10);
  return Number.isNaN(parsed) ? null : parsed;
}

export function AisleWalkthroughsTab({
  profile,
  storeId,
  storeIdParam,
  focusedAisleNumber,
  onFocusAisle,
  onOpenDigitalTwin,
}: AisleWalkthroughsTabProps) {
  const retailerLocations = profile.retailerInstoreLocations ?? [];

  const sortedWalkthroughs = useMemo(
    () => [...profile.walkthroughs].sort((a, b) => {
      if (a.dataSource === 'live' && b.dataSource !== 'live') return -1;
      if (b.dataSource === 'live' && a.dataSource !== 'live') return 1;
      return 0;
    }),
    [profile.walkthroughs],
  );

  const filteredWalkthroughs = useMemo(() => {
    if (focusedAisleNumber == null) return sortedWalkthroughs;
    const focusedLocationId = locationIdForAisleNumber(focusedAisleNumber, profile.aisles);
    return sortedWalkthroughs.filter((walk) => {
      if (focusedLocationId != null && (walk.instoreLocationIds ?? []).includes(focusedLocationId)) {
        return true;
      }
      return walk.aisleNumbers.includes(focusedAisleNumber);
    });
  }, [focusedAisleNumber, profile.aisles, sortedWalkthroughs]);

  const [selectedWalkId, setSelectedWalkId] = useState<string | undefined>(filteredWalkthroughs[0]?.id);
  const selectedWalk = filteredWalkthroughs.find((walk) => walk.id === selectedWalkId)
    ?? sortedWalkthroughs.find((walk) => walk.id === selectedWalkId)
    ?? filteredWalkthroughs[0];

  const [activeMarker, setActiveMarker] = useState<WalkthroughMarker | null>(
    selectedWalk?.timelineMarkers[0] ?? null,
  );
  const [seekToSeconds, setSeekToSeconds] = useState<number | null>(null);
  const [playbackRotationByWalkId, setPlaybackRotationByWalkId] = useState<Record<string, VideoRotationDegrees>>({});
  const [uploadOpen, setUploadOpen] = useState(false);
  const orientationDetectAttemptedRef = useRef<Set<number>>(new Set());
  const thumbnailGenerateAttemptedRef = useRef<Set<number>>(new Set());
  const queryClient = useQueryClient();
  const { toast } = useToast();

  useEffect(() => {
    if (filteredWalkthroughs.length === 0) {
      setSelectedWalkId(undefined);
      setActiveMarker(null);
      return;
    }
    const stillVisible = filteredWalkthroughs.some((walk) => walk.id === selectedWalkId);
    if (!stillVisible) {
      setSelectedWalkId(filteredWalkthroughs[0].id);
    }
  }, [focusedAisleNumber, filteredWalkthroughs, selectedWalkId]);

  useEffect(() => {
    setActiveMarker(selectedWalk?.timelineMarkers[0] ?? null);
    setSeekToSeconds(null);
  }, [selectedWalk?.id]);

  const invalidateWalkthroughQueries = async () => {
    if (!storeIdParam) return;
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['/api/store-profiles', storeIdParam, 'context'] }),
      queryClient.invalidateQueries({ queryKey: ['/api/store-profiles', storeIdParam, 'walkthroughs'] }),
      queryClient.invalidateQueries({ queryKey: ['/api/stores', storeIdParam, 'detail'] }),
    ]);
  };

  const persistRotation = async (walk: AisleWalkthrough, nextRotation: VideoRotationDegrees) => {
    if (storeId == null || walk.liveWalkthroughId == null) return;
    setPlaybackRotationByWalkId((current) => ({
      ...current,
      [walk.id]: nextRotation,
    }));
    try {
      const response = await apiRequest(
        `/api/store-profiles/${storeId}/walkthroughs/${walk.liveWalkthroughId}`,
        'PATCH',
        { videoRotationDegrees: nextRotation },
      );
      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.error || 'Failed to save rotation');
      }
      // Refresh poster so the card matches the corrected orientation.
      try {
        await apiRequest(
          `/api/store-profiles/${storeId}/walkthroughs/${walk.liveWalkthroughId}/generate-thumbnail`,
          'POST',
          { videoRotationDegrees: nextRotation },
        );
      } catch {
        // Orientation is saved even if poster regen fails.
      }
      await invalidateWalkthroughQueries();
    } catch (error) {
      setPlaybackRotationByWalkId((current) => {
        const next = { ...current };
        delete next[walk.id];
        return next;
      });
      toast({
        title: 'Rotation not saved',
        description: error instanceof Error ? error.message : 'Could not save orientation',
        variant: 'destructive',
      });
    }
  };

  // Auto-detect phone video orientation metadata for uploads that still sit at 0°
  useEffect(() => {
    if (storeId == null || !selectedWalk || selectedWalk.dataSource !== 'live') return;
    if (selectedWalk.liveWalkthroughId == null) return;
    if ((selectedWalk.videoRotationDegrees ?? 0) !== 0) return;
    if (playbackRotationByWalkId[selectedWalk.id] != null) return;
    if (orientationDetectAttemptedRef.current.has(selectedWalk.liveWalkthroughId)) return;

    orientationDetectAttemptedRef.current.add(selectedWalk.liveWalkthroughId);
    const walkthroughId = selectedWalk.liveWalkthroughId;
    const walkId = selectedWalk.id;

    void (async () => {
      try {
        const response = await apiRequest(
          `/api/store-profiles/${storeId}/walkthroughs/${walkthroughId}/detect-orientation`,
          'POST',
        );
        if (!response.ok) return;
        const payload = await response.json().catch(() => null);
        const detected = normalizeVideoRotationDegrees(payload?.videoRotationDegrees);
        if (detected === 0) return;
        setPlaybackRotationByWalkId((current) => ({
          ...current,
          [walkId]: detected,
        }));
        await invalidateWalkthroughQueries();
        toast({
          title: 'Orientation corrected',
          description: `Applied ${detected}° from video metadata (Chrome often ignores phone rotation tags).`,
        });
      } catch {
        // Detection is best-effort; manual controls remain available.
      }
    })();
  }, [playbackRotationByWalkId, selectedWalk, storeId, storeIdParam, toast]);

  // Persist real JPEG posters server-side for live uploads that still lack thumbnail_path
  useEffect(() => {
    if (storeId == null) return;
    const missing = filteredWalkthroughs.filter((walk) => (
      walk.dataSource === 'live'
      && walk.liveWalkthroughId != null
      && !walk.hasRealThumbnail
      && !thumbnailGenerateAttemptedRef.current.has(walk.liveWalkthroughId)
    ));
    if (missing.length === 0) return;

    void (async () => {
      let generatedAny = false;
      for (const walk of missing) {
        const walkthroughId = walk.liveWalkthroughId!;
        thumbnailGenerateAttemptedRef.current.add(walkthroughId);
        try {
          const response = await apiRequest(
            `/api/store-profiles/${storeId}/walkthroughs/${walkthroughId}/generate-thumbnail`,
            'POST',
            {
              videoRotationDegrees: playbackRotationByWalkId[walk.id]
                ?? normalizeVideoRotationDegrees(walk.videoRotationDegrees),
            },
          );
          if (response.ok) {
            generatedAny = true;
          }
        } catch {
          // Best-effort; card still shows a live video frame preview.
        }
      }
      if (generatedAny) {
        await invalidateWalkthroughQueries();
      }
    })();
  }, [filteredWalkthroughs, playbackRotationByWalkId, storeId, storeIdParam]);

  const selectedWalkForPlayback = selectedWalk
    ? {
        ...selectedWalk,
        videoRotationDegrees: playbackRotationByWalkId[selectedWalk.id]
          ?? normalizeVideoRotationDegrees(selectedWalk.videoRotationDegrees),
      }
    : undefined;

  const selectWalk = (walk: AisleWalkthrough) => {
    setSelectedWalkId(walk.id);
    if (walk.aisleNumbers[0] != null) {
      onFocusAisle?.(walk.aisleNumbers[0]);
    }
  };

  const handleSidePanelMarkerClick = (marker: WalkthroughMarker) => {
    setActiveMarker(marker);
    setSeekToSeconds(marker.timestampSeconds);
  };

  const canUpload = storeId != null && storeIdParam != null;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          {focusedAisleNumber != null && (
            <Badge variant="outline" className="bg-blue-50 text-blue-800 border-blue-200">
              Filtered to aisle {focusedAisleNumber}
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="ml-2 h-auto py-0 px-1 text-blue-800 hover:text-blue-950"
                onClick={() => onFocusAisle?.(null)}
              >
                Show all
              </Button>
            </Badge>
          )}
        </div>
        {canUpload && (
          <Button type="button" size="sm" onClick={() => setUploadOpen(true)}>
            <Plus className="h-4 w-4 mr-1" />
            Add walk-through
          </Button>
        )}
      </div>

      {canUpload && storeId != null && storeIdParam != null && (
        <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Add aisle walk-through</DialogTitle>
              <DialogDescription>
                Upload a video and tag the in-store locations it covers.
              </DialogDescription>
            </DialogHeader>
            <WalkthroughUploadPanel
              storeId={storeId}
              storeIdParam={storeIdParam}
              retailerInstoreLocations={retailerLocations}
              initialInstoreLocationIds={
                focusedAisleNumber != null
                  ? (() => {
                      const id = locationIdForAisleNumber(focusedAisleNumber, profile.aisles);
                      return id != null ? [id] : [];
                    })()
                  : []
              }
              onUploaded={() => setUploadOpen(false)}
            />
          </DialogContent>
        </Dialog>
      )}

      {filteredWalkthroughs.length === 0 ? (
        <Card>
          <CardContent className="py-10 text-center space-y-3">
            <p className="text-gray-500">
              {focusedAisleNumber != null
                ? `No walk-through videos tagged to aisle ${focusedAisleNumber} yet.`
                : 'No aisle walk-throughs yet.'}
            </p>
            {canUpload && (
              <Button type="button" onClick={() => setUploadOpen(true)}>
                <Plus className="h-4 w-4 mr-1" />
                Add walk-through
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {filteredWalkthroughs.map((walk) => {
              const isSelected = selectedWalk?.id === walk.id;
              const tagged = resolveWalkthroughLocationBadges(
                walk.instoreLocationIds,
                walk.aisleNumbers,
                retailerLocations,
                profile.aisles,
              );
              return (
                <Card
                  key={walk.id}
                  className={`cursor-pointer transition-all ${isSelected ? 'ring-2 ring-blue-500' : 'hover:shadow-md'}`}
                  onClick={() => selectWalk(walk)}
                >
                  <div className="relative">
                    <WalkthroughCardPreview walk={walk} />
                    <Badge className="absolute bottom-2 right-2 bg-black/70 text-white border-0">
                      {walk.durationSeconds != null && walk.durationSeconds > 0
                        ? formatWalkthroughDuration(walk.durationSeconds)
                        : '—'}
                    </Badge>
                    {walk.dataSource === 'live' && (
                      <Badge className="absolute top-2 right-2 bg-green-600 text-white border-0">Live</Badge>
                    )}
                    {walk.dataSource === 'demo' && (
                      <Badge className="absolute top-2 right-2 bg-amber-500 text-white border-0">Demo</Badge>
                    )}
                  </div>
                  <CardContent className="p-3 space-y-2">
                    <p className="font-medium text-sm line-clamp-2">{walk.title}</p>
                    <div className="flex flex-wrap gap-1">
                      {tagged.length === 0 ? (
                        <span className="text-xs text-gray-500">No locations tagged</span>
                      ) : (
                        tagged.slice(0, 4).map((item) => (
                          <Badge key={item.key} variant="outline" className="text-xs font-normal">
                            {item.label}
                          </Badge>
                        ))
                      )}
                      {tagged.length > 4 && (
                        <Badge variant="outline" className="text-xs font-normal">
                          +{tagged.length - 4}
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-500">{walk.captureDate}</span>
                      <Badge variant="outline" className={statusColors[walk.processingStatus]}>
                        {STATUS_LABELS[walk.processingStatus]}
                      </Badge>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {selectedWalk && (
            <Card>
              <CardHeader className="pb-2">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div>
                    <CardTitle className="text-base">{selectedWalk.title}</CardTitle>
                    <p className="text-sm text-gray-500">{selectedWalk.routeDescription}</p>
                  </div>
                  {selectedWalk.aisleNumbers[0] != null && onOpenDigitalTwin && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => onOpenDigitalTwin(selectedWalk.aisleNumbers[0])}
                    >
                      Open in Digital Twin
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex flex-col lg:flex-row gap-4">
                  <div className="flex-1 space-y-3">
                    <WalkthroughVideoPlayer
                      walk={selectedWalkForPlayback ?? selectedWalk}
                      activeMarker={activeMarker}
                      onMarkerChange={setActiveMarker}
                      onMarkerClick={setActiveMarker}
                      seekToSeconds={seekToSeconds}
                      onSeekHandled={() => setSeekToSeconds(null)}
                      storeId={storeId}
                      onRotationChange={(degrees) => {
                        void persistRotation(selectedWalk, degrees);
                      }}
                      onThumbnailCaptured={() => {
                        void invalidateWalkthroughQueries();
                      }}
                    />
                    {storeId != null && storeIdParam && (
                      <WalkthroughAisleTagsPanel
                        walk={selectedWalk}
                        storeId={storeId}
                        storeIdParam={storeIdParam}
                        retailerInstoreLocations={retailerLocations}
                        onFocusAisle={onFocusAisle}
                      />
                    )}
                  </div>

                  <div className="lg:w-72 space-y-4">
                    <div className="space-y-2">
                      <p className="text-sm font-medium">Timeline markers</p>
                      {selectedWalk.timelineMarkers.length === 0 ? (
                        <p className="text-sm text-gray-500">
                          No markers yet. Play and pause using the controls below the video.
                        </p>
                      ) : (
                        selectedWalk.timelineMarkers.map((marker) => (
                          <button
                            key={marker.id}
                            type="button"
                            onClick={() => handleSidePanelMarkerClick(marker)}
                            className={`w-full text-left border rounded-lg p-3 text-sm transition-all ${
                              activeMarker?.id === marker.id ? 'border-blue-500 bg-blue-50' : 'hover:bg-gray-50'
                            }`}
                          >
                            <p className="font-medium">{formatWalkthroughDuration(marker.timestampSeconds)}</p>
                            <p className="mt-1">{marker.label ?? marker.campaignName}</p>
                          </button>
                        ))
                      )}
                    </div>

                    {storeId != null && storeIdParam && (
                      <WalkthroughRecordEditor
                        walk={selectedWalkForPlayback ?? selectedWalk}
                        storeId={storeId}
                        storeIdParam={storeIdParam}
                        retailerInstoreLocations={retailerLocations}
                        onDeleted={() => setSelectedWalkId(undefined)}
                        onRotationChange={(degrees) => {
                          setPlaybackRotationByWalkId((current) => ({
                            ...current,
                            [selectedWalk.id]: degrees,
                          }));
                        }}
                      />
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
